import { Fragment } from 'react';

import { StepProps, useAutoNavigate } from '@betterment/js-runtime';
import { Box, Button, FormikField as Field, RadioGroup, Text } from '@betterment/samba-react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import StepHeading from '../../AccountCreationFlow/components/StepHeading';
import { InvestingExperienceLevel } from '../../graphql/schema';
import SecureInfoBadge from '../../Shared/components/SecureInfoBadge';
import { WorkflowLayout } from '../../Shared/layout/WorkflowLayout';
import { AutomatedInvestingGuidanceBox } from '../components/AutomatedInvestingGuidanceBox';
import { DirectTradingFlowStateValues } from '../Controller';

const schema = () =>
  Yup.object().shape({
    investingExperience: Yup.string().required('A selection is required'),
  });

const investingExperienceOptions = [
  {
    value: InvestingExperienceLevel.New,
    label: 'New',
    description: 'I am just starting out with investing.',
  },
  {
    value: InvestingExperienceLevel.Limited,
    label: 'Limited',
    description: "I've made a few investments.",
  },
  {
    value: InvestingExperienceLevel.Moderate,
    label: 'Moderate',
    description: "I'm comfortable making investment decisions.",
  },
  {
    value: InvestingExperienceLevel.Advanced,
    label: 'Advanced',
    description: 'I have a lot of investing experience.',
  },
];

export const InvestingExperienceStep: React.FC<StepProps<DirectTradingFlowStateValues>> = ({
  goToNextStep,
  goToPreviousStep,
  flowState,
}) => {
  const navigate = useAutoNavigate();

  const handleSubmit = (values: { investingExperience: InvestingExperienceLevel }) => {
    goToNextStep({ investingExperience: values.investingExperience });
  };

  return (
    <WorkflowLayout
      feature="add_direct_trading_account"
      pageTitle="Investing Experience"
      workflowTitle=""
      onBack={goToPreviousStep}
      onClose={() => navigate(flowState.returnToPath)}
    >
      <Box css={{ marginBottom: 'space.400' }}>
        <StepHeading>Start by confirming your investing experience.</StepHeading>
      </Box>
      <Formik
        validationSchema={schema()}
        initialValues={{ investingExperience: flowState.values.investingExperience }}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        enableReinitialize={true}
      >
        {({ values, isSubmitting }) => (
          <Form>
            <Field name="investingExperience">
              <Field.Control>
                <RadioGroup css={{ gap: 'space.0', borderBottom: '1px solid', borderColor: 'border.weak' }}>
                  {investingExperienceOptions.map(option => (
                    <Fragment key={option.value}>
                      <RadioGroup.ItemContainer
                        key={option.value}
                        css={{
                          borderTop: '1px solid',
                          borderColor: 'border.weak',
                          paddingY: 'space.300',
                          gap: 'space.100',
                        }}
                      >
                        <RadioGroup.Item id={`investing-experience-${option.value}`} value={option.value} />
                        <RadioGroup.ItemLabel
                          htmlFor={`investing-experience-${option.value}`}
                          css={{ display: 'grid', cursor: 'pointer' }}
                        >
                          <Text variant="text100">{option.label}</Text>
                          <Text variant="text50" color="text.weak">
                            {option.description}
                          </Text>
                        </RadioGroup.ItemLabel>
                      </RadioGroup.ItemContainer>
                      {values.investingExperience === InvestingExperienceLevel.New &&
                        option.value === InvestingExperienceLevel.New && <AutomatedInvestingGuidanceBox />}
                    </Fragment>
                  ))}
                </RadioGroup>
              </Field.Control>
              <Field.ErrorMessage />
            </Field>

            <Button type="submit" variant="solidPrimary" css={{ marginTop: 'space.400' }} disabled={isSubmitting}>
              Continue
            </Button>
          </Form>
        )}
      </Formik>
      <Box css={{ display: 'flex', alignItems: 'center', gap: 'space.25', marginTop: 'space.200' }}>
        <SecureInfoBadge />
      </Box>
    </WorkflowLayout>
  );
};
